// src/layouts/LoggedInLayout/Components/Sidebar/Sidebar.tsx
import React, { useState } from 'react';
import {
  FiHome,
  FiFileText,
  FiBookOpen,
  FiAward,
  FiHelpCircle,
  FiMessageSquare,
  FiChevronDown,
  FiBarChart2,
  FiUsers,
  FiCalendar,
  FiShield,
  FiUserPlus,
  FiSearch,
  FiHeart,
  FiGrid,
  FiBook,
  FiTool,
  FiSettings,
  FiUpload,
  FiBell,
  FiLayers,
  FiFolder,
  FiClipboard,
  FiTarget,
  FiTrendingUp,
  FiPackage,
} from 'react-icons/fi';
import { CiTrophy } from "react-icons/ci";
import { HiOutlineSparkles } from "react-icons/hi";
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/useAuthStore';
import { UserRole } from '../../../constants/roles';

interface MenuItem {
  label: string;
  icon?: React.ReactNode;
  path?: string;
  submenu?: MenuItem[];
  active?: boolean;
  isCollapsible?: boolean;
}

interface SidebarProps {
  forceExpanded?: boolean; // NEW PROP
}

const Sidebar: React.FC<SidebarProps> = ({ forceExpanded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  const [myLearningsOpen, setMyLearningsOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  // Trainer collapsible states
  const [trainerMyLearningsOpen, setTrainerMyLearningsOpen] = useState(false);

  // This decides whether to show text or just icons
  const isSidebarExpanded = forceExpanded || isExpanded;

  // Determine user role
  const userRole = user?.role as UserRole;
  const isAdmin = userRole === UserRole.ADMIN;
  const isTrainee = userRole === UserRole.TRAINEE;
  const isTrainer = userRole === UserRole.TRAINER;
  const isSuperAdmin = userRole === UserRole.SUPER_ADMIN;

  // Admin Menu Configuration
  const adminMainMenu: MenuItem[] = [
    { label: 'Dashboard', icon: <FiGrid />, path: '/admin/dashboard' },
  ];

  const adminContentMenu: MenuItem[] = [
    // { label: 'Content Architect', icon: <FiLayers />, path: '/admin/content/architect' },
    { label: 'Content Management', icon: <FiFileText />, path: '/admin/content/management' },
    { label: 'Course Management', icon: <FiBook />, path: '/admin/content/courses' },
    { label: 'Catalog / Category', icon: <FiFolder />, path: '/admin/content/catalog' },
    { label: 'Assessments', icon: <FiClipboard />, path: '/admin/content/assessments' },
    { label: 'Group Projects', icon: <FiUsers />, path: '/admin/content/group-projects' },
    { label: 'Certification Management', icon: <FiAward />, path: '/admin/content/certifications' },
  ];

  const adminUserMenu: MenuItem[] = [
    { label: 'My Team', icon: <FiUsers />, path: '/admin/users/team' },
    { label: 'User', icon: <FiUserPlus />, path: '/admin/users/list' },
    { label: 'Dynamic Assignment', icon: <FiTarget />, path: '/admin/users/assignments' },
    { label: 'Learning Assignment Tool', icon: <FiClipboard />, path: '/admin/users/learning-assignment' },
    { label: 'Performance', icon: <FiTrendingUp />, path: '/admin/users/performance' },
  ];

  const adminReportsMenu: MenuItem[] = [
    { label: 'Reports', icon: <FiBarChart2 />, path: '/admin/reports' },
  ];

  const adminToolsMenu: MenuItem[] = [
    { label: 'Bulk Uploader', icon: <FiUpload />, path: '/admin/tools/bulk-uploader' },
    { label: 'Feedback & Survey', icon: <FiMessageSquare />, path: '/admin/tools/feedback' },
    { label: 'Marketing / Notifications', icon: <FiBell />, path: '/admin/tools/marketing' },
    { label: 'ILT Resource Management', icon: <FiPackage />, path: '/admin/tools/ilt-resources' },
  ];

  const adminSystemMenu: MenuItem[] = [
    { label: 'Support & Help', icon: <FiHelpCircle />, path: '/admin/support' },
    { label: 'Settings', icon: <FiSettings />, path: '/admin/settings' },
  ];

  // Trainee Menu Configuration
  const traineeMainMenu: MenuItem[] = [
    { label: 'Home', icon: <FiHome />, path: '/trainee/home' },
    { label: 'Transcript', icon: <FiFileText />, path: '/trainee/transcript' },
    {
      label: 'My Learnings',
      icon: <FiBookOpen />,
      submenu: [
        { label: 'Browse Courses', path: '/trainee/my-learnings/browse-courses' },
        { label: 'My Course', path: '/trainee/my-learnings/my-course' },
        { label: 'Wishlist', path: '/trainee/my-learnings/wishlist' },
      ],
      isCollapsible: true,
    },
  ];

  const traineeLearningsMenu: MenuItem[] = [
    { label: 'Achievements', icon: <CiTrophy />, path: '/trainee/achievements' },
    { label: 'Certifications', icon: <FiAward />, path: '/trainee/certifications' },
  ];

  const traineeToolsMenu: MenuItem[] = [
    { label: 'AI Learning Paths', icon: <FiHelpCircle />, path: '/trainee/ai-paths' },
    { label: 'Support & Help', icon: <FiMessageSquare />, path: '/trainee/support' },
  ];

  const traineeFeaturesOnDemand: MenuItem[] = [
    { label: 'Performance', icon: <FiBarChart2 />, path: '/trainee/performance' },
    { label: 'My Team', icon: <FiUsers />, path: '/trainee/team' },
    { label: 'Lingo Lab AI', icon: <HiOutlineSparkles />, path: '/trainee/lingo-lab' },
    { label: 'Carve Assessment', icon: <FiCalendar />, path: '/trainee/assessment' },
    { label: 'AI Proctor', icon: <FiShield />, path: '/trainee/proctor' },
    { label: 'Group Projects', icon: <FiUserPlus />, path: '/trainee/group-projects' },
  ];

  // Trainer Menu Configuration
  const trainerMainMenu: MenuItem[] = [
    { label: 'Dashboard', icon: <FiGrid />, path: '/trainer/dashboard' },
    {
      label: 'My Learnings',
      icon: <FiBookOpen />,
      submenu: [
        { label: 'Browse Course', path: '/trainer/my-learnings/browse-courses' },
        { label: 'My Courses', path: '/trainer/my-learnings/my-courses' },
        { label: 'Wishlist', path: '/trainer/my-learnings/wishlist' },
      ],
    },
  ];

  const trainerContentMenu: MenuItem[] = [
    { label: 'Course Management', icon: <FiBook />, path: '/trainer/content/course-management' },
    { label: 'Assessment', icon: <FiClipboard />, path: '/trainer/content/assessments' },
    { label: 'Group Project', icon: <FiUsers />, path: '/trainer/content/group-projects' },
  ];

  const trainerUserMenu: MenuItem[] = [
    { label: 'My Team', icon: <FiUsers />, path: '/trainer/users/my-team' },
    { label: 'User', icon: <FiUserPlus />, path: '/trainer/users/user-list' },
    { label: 'Learning Assignment Tool', icon: <FiTarget />, path: '/trainer/users/learning-assignment' },
    { label: 'Performance', icon: <FiTrendingUp />, path: '/trainer/users/performance' },
  ];

  const trainerReportsMenu: MenuItem[] = [
    { label: 'Reports', icon: <FiBarChart2 />, path: '/trainer/reports' },
  ];

  const trainerToolsMenu: MenuItem[] = [
    { label: 'Marketing / Notification', icon: <FiBell />, path: '/trainer/tools/marketing' },
    { label: 'ILT Resource Management', icon: <FiPackage />, path: '/trainer/tools/ilt-resources' },
  ];

  const trainerSystemMenu: MenuItem[] = [
    { label: 'Support & Help', icon: <FiHelpCircle />, path: '/trainer/support' },
  ];

  // Super Admin Menu Configuration
  const superAdminMainMenu: MenuItem[] = [
    { label: 'Dashboard', icon: <FiGrid />, path: '/superadmin/dashboard' },
  ];

  const superAdminContentMenu: MenuItem[] = [
    { label: 'Content Architect', icon: <FiLayers />, path: '/superadmin/content/content-architect' },
    { label: 'Content Management', icon: <FiFileText />, path: '/superadmin/content/content-management' },
    { label: 'Course Management', icon: <FiBook />, path: '/superadmin/content/course-management' },
    { label: 'Catalog / Category', icon: <FiFolder />, path: '/superadmin/content/catalog' },
    { label: 'Assessments', icon: <FiClipboard />, path: '/superadmin/content/assessments' },
    { label: 'Group Projects', icon: <FiUsers />, path: '/superadmin/content/group-projects' },
    { label: 'Certification Management', icon: <FiAward />, path: '/superadmin/content/certifications' },
    { label: 'Training Assistant', icon: <HiOutlineSparkles />, path: '/superadmin/content/training-assistant' },
  ];

  const superAdminUserMenu: MenuItem[] = [
    { label: 'My Team', icon: <FiUsers />, path: '/superadmin/users/my-team' },
    { label: 'User', icon: <FiUserPlus />, path: '/superadmin/users/user-list' },
    { label: 'Organization Hierarchy', icon: <FiLayers />, path: '/superadmin/users/org-hierarchy' },
    { label: 'Performance Tracking', icon: <FiTrendingUp />, path: '/superadmin/users/performance-tracking' },
    { label: 'Dynamic Assessment', icon: <FiTarget />, path: '/superadmin/users/dynamic-assessment' },
    { label: 'Learning Assignment Tool', icon: <FiClipboard />, path: '/superadmin/users/learning-assignment' },
    { label: 'Security and Permissions', icon: <FiShield />, path: '/superadmin/users/security' },
  ];

  const superAdminToolsMenu: MenuItem[] = [
    { label: 'Bulk Uploader', icon: <FiUpload />, path: '/superadmin/tools/bulk-uploader' },
    { label: 'Feedback and Survey', icon: <FiMessageSquare />, path: '/superadmin/tools/feedback' },
    { label: 'Marketing / Notifications', icon: <FiBell />, path: '/superadmin/tools/marketing' },
    { label: 'ILT Resource Management', icon: <FiPackage />, path: '/superadmin/tools/ilt-resources' },
  ];

  const superAdminReportsMenu: MenuItem[] = [
    { label: 'Report and Analytics', icon: <FiBarChart2 />, path: '/superadmin/reports' },
  ];

  const superAdminSystemMenu: MenuItem[] = [
    { label: 'Support & Help', icon: <FiHelpCircle />, path: '/superadmin/support' },
    { label: 'Settings', icon: <FiSettings />, path: '/superadmin/settings' },
  ];

  const renderMenuItem = (item: MenuItem, isSubmenu = false) => {
    const hasSubmenu = item.submenu && item.submenu.length > 0;
    const isActive =
      !!item.path &&
      (location.pathname === item.path ||
        // Treat the index route (/workspace) as active when we are on nested paths like /workspace/
        (item.path === '/workspace' && location.pathname === '/workspace/'));
    const isOpen = item.label === 'My Learnings' ? myLearningsOpen : featuresOpen;

    return (
      <div key={item.label}>
        <div
          className={`logged-in-menu-item ${isActive ? 'logged-in-active' : ''} ${isSubmenu ? 'pl-10' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (item.isCollapsible || hasSubmenu) {
              if (item.label === 'My Learnings') setMyLearningsOpen(!myLearningsOpen);
              if (item.label === 'FEATURES ON DEMAND') setFeaturesOpen(!featuresOpen);
            }
            if (item.path) {
              navigate(item.path);
            }
          }}
          style={{
            justifyContent: hasSubmenu || item.isCollapsible ? 'space-between' : 'flex-start',
          }}
        >
          <div className="flex items-center gap-3">
            {!isSubmenu && item.icon}
            <span className="logged-in-menu-item-text">{item.label}</span>
          </div>

          {(hasSubmenu || item.isCollapsible) && (
            <FiChevronDown
              className={`logged-in-dropdown-arrow ${isOpen ? 'logged-in-open' : ''}`}
            />
          )}
        </div>

        {/* Render actual submenu if exists */}
        {hasSubmenu && isOpen && (
          <div className="logged-in-submenu logged-in-open">
            {item.submenu!.map((sub) => renderMenuItem(sub, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <style>{`
        .logged-in-sidebar {
          background-color: var(--color-white);
          overflow-y: auto;
          border-radius: 1rem;
          padding: 1.5rem 0.5rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          height: 100%;
          transition: all 0.3s ease;
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
        }
          [data-theme="dark"] .logged-in-sidebar {
            background-color: var(--color-primary-dark);
            border:none;
          }
        
        .logged-in-sidebar::-webkit-scrollbar {
          display: none; /* Chrome, Safari, Opera */
        }
        
        .logged-in-sidebar.expanded {
          padding: 1.5rem 1rem;
        }
        
        .logged-in-menu-title {
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 0 8px;
          margin-bottom: 16px;
          display: block;
          white-space: nowrap;
          text-align: center;
          transition: all 0.3s ease;
        }
          [data-theme="dark"] .logged-in-menu-title { color: var(--color-grey); }
        
        .logged-in-sidebar.expanded .logged-in-menu-title {
          font-size: 13px;
          text-align: left;
          margin-bottom: 24px;
        }
        
        .logged-in-menu-item {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          margin-bottom: 4px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s;
          color: #4b5563;
          font-size: 13px;
          position: relative;
        }
          [data-theme="dark"] .logged-in-menu-item { color: var(--color-grey) }
          
          .logged-in-menu-item:hover{
          color: var(-color-black);
          }
        [data-theme="dark"] .logged-in-menu-item:hover {
        color: var(--color-white);
        }
        
        .logged-in-sidebar:not(.expanded) .logged-in-menu-item {
          justify-content: center;
          padding: 12px 8px;
        }
        
        .logged-in-sidebar:not(.expanded) .logged-in-menu-item .flex {
          justify-content: center;
          width: 100%;
        }
        
        .logged-in-menu-item.logged-in-active {
          background-color: var(--color-primary);
          color: var(--color-white);
        }
          [data-theme="dark"] .logged-in-menu-item.logged-in-active {
            background-color: var(--color-sunflare);
            color: var(--color-white);
          }
        
        .logged-in-menu-item svg {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
        }
        
        .logged-in-menu-item-text {
          white-space: nowrap;
          overflow: hidden;
          transition: opacity 0.3s ease, width 0.3s ease;
        }
        
        .logged-in-sidebar:not(.expanded) .logged-in-menu-item-text {
          opacity: 0;
          width: 0;
        }
        
        .logged-in-dropdown-arrow {
          width: 14px;
          height: 14px;
          transition: transform 0.3s ease, opacity 0.3s ease;
        }
        
        .logged-in-sidebar:not(.expanded) .logged-in-dropdown-arrow {
          opacity: 0;
          width: 0;
        }
        
        .logged-in-dropdown-arrow.logged-in-open {
          transform: rotate(180deg);
        }
        
        .logged-in-section-title {
          font-size: 10px;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          margin: 20px 0 8px 0;
          padding: 0 8px;
          white-space: nowrap;
          text-align: center;
          transition: all 0.3s ease;
        }
        
        .logged-in-sidebar.expanded .logged-in-section-title {
          font-size: 12px;
          text-align: left;
          margin: 24px 0 12px 16px;
          letter-spacing: 0.5px;
        }
        
        .logged-in-submenu {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.4s ease;
        }
        
        .logged-in-submenu.logged-in-open {
          max-height: 1000px;
        }
        
        .pl-10 {
          padding-left: 40px !important;
          font-size: 12px !important;
        }
        
        .logged-in-sidebar:not(.expanded) .pl-10 {
          padding-left: 12px !important;
        }
        
        .logged-in-menu-item .flex {
          gap: 12px !important;
        }
        
        .logged-in-sidebar:not(.expanded) .logged-in-menu-item .flex {
          gap: 0 !important;
        }
            @media (max-width: 500px) {
   
        .logged-in-menu-item {
          font-size: 12px;
        }

           
        .logged-in-dropdown-arrow {
          width: 10px;
          height: 10px;
        }
          }
      `}</style>

   <div
        className={`logged-in-sidebar ${isSidebarExpanded ? 'expanded' : ''}`}
        onMouseEnter={() => !forceExpanded && setIsExpanded(true)}
        onMouseLeave={() => {
          if (!forceExpanded) {
            setIsExpanded(false);
            setMyLearningsOpen(false);
          }
        }}
      >
        {/* Render Admin Sidebar */}
        {isAdmin && (
          <>
            {adminMainMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'Content Management' : 'Content'}
            </div>
            {adminContentMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'User Management' : 'Users'}
            </div>
            {adminUserMenu.map(item => renderMenuItem(item))}

            {adminReportsMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'Tools' : 'Tools'}
            </div>
            {adminToolsMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'System' : 'Sys'}
            </div>
            {adminSystemMenu.map(item => renderMenuItem(item))}
          </>
        )}

        {/* Render Trainee Sidebar */}
        {isTrainee && (
          <>
            <span className="logged-in-menu-title">
              {isSidebarExpanded ? 'Main Menu' : 'Menu'}
            </span>

            {traineeMainMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'Learnings' : 'Learn'}
            </div>
            {traineeLearningsMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">Tools</div>
            {traineeToolsMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'Features on Demand' : 'Features'}
            </div>
            {traineeFeaturesOnDemand.map(item => renderMenuItem(item))}
          </>
        )}

        {/* Render Trainer Sidebar */}
        {isTrainer && (
          <>
            {trainerMainMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'Content Management' : 'Content'}
            </div>
            {trainerContentMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'User Management' : 'Users'}
            </div>
            {trainerUserMenu.map(item => renderMenuItem(item))}

            {trainerReportsMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'Tools' : 'Tools'}
            </div>
            {trainerToolsMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'System' : 'Sys'}
            </div>
            {trainerSystemMenu.map(item => renderMenuItem(item))}
          </>
        )}

        {/* Render Super Admin Sidebar */}
        {isSuperAdmin && (
          <>
            {superAdminMainMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'Content Management' : 'Content'}
            </div>
            {superAdminContentMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'User Management' : 'Users'}
            </div>
            {superAdminUserMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'Tools' : 'Tools'}
            </div>
            {superAdminToolsMenu.map(item => renderMenuItem(item))}

            {superAdminReportsMenu.map(item => renderMenuItem(item))}

            <div className="logged-in-section-title">
              {isSidebarExpanded ? 'System' : 'Sys'}
            </div>
            {superAdminSystemMenu.map(item => renderMenuItem(item))}
          </>
        )}
      </div>
    </>
  );
};

export default Sidebar;